// Retken pistevarasto (IndexedDB).
//
// Jokainen GPS-piste kirjoitetaan omana, pienenä transaktionaan. Näin yhden pisteen
// tallennus ei maksa enempää kuin retken pituus, eikä pisteitä menetetä siksi, että
// koko retken tila kirjoitettaisiin uudelleen (kuten localStorage-kuvassa).
//
// Pisteen elinkaari:
//   1. append    – piste kirjoitetaan varastoon heti, kun laite antaa sen
//   2. (lähetys) – piste lähetetään palvelimelle erässä
//   3. ackThrough – piste poistetaan VASTA, kun palvelin on kuitannut erän
//   4. dropTrip  – retken päätyttyä jäljellä olevat pisteet siivotaan
//
// Jos sovellus kuolee kohtien 2 ja 3 välillä, piste lähetetään uudelleen. Se on
// turvallista, koska palvelin tunnistaa pisteen retken UUID:stä ja juoksevasta
// numerosta (seq) eikä tuplaa sitä.
//
// Tämä tiedosto ei tunne Recorderia: se on vain varasto. Fabrikka ottaa
// indexedDB-tehtaan parametrina, jotta testit voivat käyttää väärennettyä.

const DB_NAME = "tilehunting-points";
const STORE = "points";

/** Tallennettava piste. Muut kentät (esim. lisämerkinnät) eivät päädy varastoon. */
function toRecord(tripId, p) {
  return { tripId, seq: p.seq, t: p.t, lat: p.lat, lon: p.lon, accuracy: p.accuracy ?? null };
}

function fromRecord(r) {
  return { seq: r.seq, t: r.t, lat: r.lat, lon: r.lon, accuracy: r.accuracy };
}

export function createPointJournal({ idb = globalThis.indexedDB } = {}) {
  let opening = null;

  /** Avaa tietokannan kerran ja pitää yhteyden auki. Epäonnistunut avaus yritetään uudelleen. */
  function open() {
    if (opening) return opening;
    opening = new Promise((resolve, reject) => {
      if (!idb) return reject(new Error("IndexedDB ei ole käytettävissä"));
      const request = idb.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        // Avain [tripId, seq]: pisteet ovat retkittäin seq-järjestyksessä ja
        // yksi retki voidaan lukea tai poistaa avainvälillä.
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE, { keyPath: ["tripId", "seq"] });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // Selain voi sulkea yhteyden (esim. päivitys): seuraava kutsu avaa uuden.
        db.onclose = () => { opening = null; };
        db.onversionchange = () => { db.close(); opening = null; };
        resolve(db);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("IndexedDB-avaus esti toinen yhteys"));
    }).catch((error) => {
      opening = null;
      throw error;
    });
    return opening;
  }

  /**
   * Ajaa toiminnon yhdessä kirjoitus- tai lukutransaktiossa ja odottaa, että
   * transaktio on valmis (oncomplete), ei vain että pyyntö on jätetty.
   * "strict"-kestävyys pyytää selainta tyhjentämään kirjoituksen levylle; vanhemmat
   * selaimet ohittavat valinnan.
   */
  async function run(mode, work) {
    const db = await open();
    let tx;
    try {
      tx = db.transaction(STORE, mode, mode === "readwrite" ? { durability: "strict" } : undefined);
    } catch (error) {
      if (error?.name === "InvalidStateError") { opening = null; throw error; }
      tx = db.transaction(STORE, mode);
    }
    return new Promise((resolve, reject) => {
      let result;
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB-transaktio keskeytyi"));
      result = work(tx.objectStore(STORE));
      // getAll-pyynnön tulos luetaan vasta, kun transaktio on valmis.
      if (result && "onsuccess" in result) {
        const request = result;
        result = undefined;
        request.onsuccess = () => { result = request.result; };
      }
    });
  }

  return {
    /** Yksi piste, oma transaktio. Lupaus täyttyy vasta, kun kirjoitus on valmis. */
    append: (tripId, point) => run("readwrite", (store) => { store.put(toRecord(tripId, point)); }),

    /** Monta pistettä yhdessä transaktiossa (esim. vanhan tallennuksen siirto). */
    appendMany: (tripId, points) => run("readwrite", (store) => { points.forEach((p) => store.put(toRecord(tripId, p))); }),

    /** Kaikki retkien pisteet: Map tripId -> pisteet seq-järjestyksessä. */
    async readAll() {
      const rows = await run("readonly", (store) => store.getAll());
      const byTrip = new Map();
      for (const row of rows ?? []) {
        if (!byTrip.has(row.tripId)) byTrip.set(row.tripId, []);
        byTrip.get(row.tripId).push(fromRecord(row));
      }
      for (const points of byTrip.values()) points.sort((a, b) => a.seq - b.seq);
      return byTrip;
    },

    /** Poistaa retken pisteet numeroon seq asti (mukaan lukien): palvelin on kuitannut ne. */
    ackThrough: (tripId, seq) =>
      run("readwrite", (store) => { store.delete(IDBKeyRange.bound([tripId, -Infinity], [tripId, seq])); }),

    /** Poistaa kaikki retken pisteet. */
    dropTrip: (tripId) =>
      run("readwrite", (store) => { store.delete(IDBKeyRange.bound([tripId, -Infinity], [tripId, Infinity])); }),

    /** Sulkee yhteyden (testit). */
    async close() {
      if (!opening) return;
      const db = await opening.catch(() => null);
      opening = null;
      db?.close();
    },
  };
}
